import { useState, useMemo } from "react";
import { Typography, Box } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import MainDialogBox from "../components/Dialog/MainDialogBox";
import DataGridSkeleton from "../components/DataGridSkeleton";
import CustomPagination from "./CustomPagination";
import { useTrainerCoursesList } from "../../assets/api/useAdminApi";
import CustomDivider from "./CustomDivider";
import SelectBoxField from "../components/formfields/SelectBoxField";

export const useTrainerColumnsWithDialog = () => {
    const [selectedRow, setSelectedRow] = useState({});
    const [open, setOpen] = useState(false);
    const [filter, setFilter] = useState({});
    const [paginationModelCourse, setPaginationModelCourse] = useState({ page: 0, pageSize: 5 });

    const { data: CourseList, isLoading: isCourseListLoading } = useTrainerCoursesList({
        ...filter,
        trainer_id: selectedRow?.id,
    });

    const courseRows = useMemo(() => {
        return CourseList?.data?.map((item, index) => ({
            ...item,
            id: `${item?.id}_${index}`,
            sr_no: index + 1,
        })) || [];
    }, [CourseList]);

    const viewTrainerCourse = (row) => {
        setSelectedRow(row);
        setOpen(true);
    };

    const handleClose = () => setOpen(false);

    const CourseOptions = useMemo(() => {
        const unique = new Map();
        CourseList?.data?.forEach((item) => {
            if (!unique.has(item.title)) unique.set(item.title, item);
        });
        return Array.from(unique.values()).map((item) => ({
            id: item.event_id,
            value: item.title,
            label: item.title,
        }));
    }, [CourseList]);

    const ApartmentOptions = useMemo(() => {
        const unique = new Map();
        CourseList?.data?.forEach((item) => {
            if (!unique.has(item.apartment_name)) unique.set(item.apartment_name, item);
        });
        return Array.from(unique.values()).map((item) => ({
            id: item.apartment_name,
            value: item.apartment_name,
            label: item.apartment_name,
        }));
    }, [CourseList]);

    const handleChangeTitle = (value) =>
        setFilter((prev) => ({ ...prev, course_id: value }));
    const handleClearTitle = () =>
        setFilter((prev) => {
            const { course_id, ...rest } = prev;
            return rest;
        });
    const handleChangeApartment = (value) =>
        setFilter((prev) => ({ ...prev, apartment_name: value }));
    const handleClearApartment = () =>
        setFilter((prev) => {
            const { apartment_name, ...rest } = prev;
            return rest;
        });

    const courseColumns = [
        { field: "sr_no", headerName: "Sr No",   width: 50, renderCell: (params) => <Typography>{params.value}</Typography> },
        { field: "title", headerName: "Course Name",   width: 200, renderCell: (params) => <Typography fontSize={12}>{params.value}</Typography> },
        { field: "apartment_name", headerName: "Apartment Name",   width: 200, renderCell: (params) => <Typography fontSize={12}>{params.value}</Typography> },
        { field: "day", headerName: "Day",   width: 100, renderCell: (params) => <Typography fontSize={12}>{params.value}</Typography> },
        { field: "start_date", headerName: "Start Date",  width: 100, renderCell: (params) => <Typography fontSize={12}>{params.value}</Typography> },
        { field: "end_date", headerName: "End Date",   width: 100, renderCell: (params) => <Typography fontSize={12}>{params.value}</Typography> },
        { field: "start_time", headerName: "Start Time",   width: 100, renderCell: (params) => <Typography fontSize={12}>{params.value}</Typography> },
        { field: "end_time", headerName: "End Time",   width: 100, renderCell: (params) => <Typography fontSize={12}>{params.value}</Typography> },
        { field: "is_expired", headerName: "Status",   width: 100, renderCell: (params) => <Typography fontSize={12}>{params.value === 1 ? "Expired" : "Live"}</Typography> },
    ];

    const trainerColumns = [
        {
            field: "sr_no",
            headerName: "Sr No",
            width: 100,
            renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
        },
        {
            field: "firstname",
            headerName: "Trainer Name",
            width: 200,
            renderCell: (params) => (
                <Typography variant="body2">
                    {params.value} {params.row.lastname}
                </Typography>
            ),
        },
        {
            field: "id",
            headerName: "Trainer Id",
            width: 100,
            renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
        },
        {
            field: "mobile",
            headerName: "Mobile Number",
            width: 150,
            renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
        },
        {
            field: "email",
            headerName: "E-Mail ID",
            width: 300,
            renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
        },
        {
            field: "assignment_status",
            headerName: "Status",
            width: 100,
            renderCell: (params) => (
                <Typography variant="body2">{params.value === 1 ? "Assigned" : "Unassigned"}</Typography>
            ),
        },
    ];

    const dialogJSX = (
        <MainDialogBox open={open} handleClose={()=>{setFilter();handleClose();}} width={"60%"}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                <Typography variant="h3" fontWeight={"bold"}>
                    {selectedRow.firstname} {selectedRow.lastname}
                </Typography>
                <Box sx={{ width: "40%" }}>
                    <SelectBoxField
                        label="Course Name"
                        options={CourseOptions}
                        isRequired
                        onChange={handleChangeTitle}
                        ClearFunc={handleClearTitle}
                        isloading={isCourseListLoading}
                    />
                </Box>
                <Box sx={{ width: "40%" }}>
                    <SelectBoxField
                        label="Apartment Name"
                        options={ApartmentOptions}
                        isRequired
                        onChange={handleChangeApartment}
                        ClearFunc={handleClearApartment}
                        isloading={isCourseListLoading}
                    />
                </Box>
            </Box>

            <Box sx={{ display: "flex", gap: "10px" }}>
                <Typography variant="body1" my={2}>
                    Trainer ID: {selectedRow.id}
                </Typography>
            </Box>

            <CustomDivider />

            {isCourseListLoading ? (
                <DataGridSkeleton rowsNum={5} />
            ) : (
                <DataGrid
                    rows={courseRows}
                    columns={courseColumns}
                    getRowId={(row) => row.id}
                    pagination
                    paginationModel={paginationModelCourse}
                    onPaginationModelChange={setPaginationModelCourse}
                    pageSizeOptions={[5]}
                    slots={{
                        pagination: () => (
                            <CustomPagination
                                paginationModel={paginationModelCourse}
                                setPaginationModel={setPaginationModelCourse}
                                totalRows={courseRows?.length || 0}
                                pageSize={paginationModelCourse.pageSize}
                            />
                        ),
                    }}
                />
            )}
        </MainDialogBox>
    );

    return { trainerColumns, dialogJSX, viewTrainerCourse };
};

